/**
 * Componente AdminDashboardCharts
 * Card com três gráficos de pizza para o dashboard do Admin
 * 
 * @author Equipe GestaoeCRM
 * @date 05/11/2025
 * @version 1.0.0
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { dashboardApi } from '@/services/api';
import LoadingSpinner from './LoadingSpinner';
import { AlertCircle, RefreshCw } from 'lucide-react';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface ChartDataItem {
  name: string;
  value: number;
}

interface ChartData {
  data: ChartDataItem[];
}

// ============================================================================
// CONFIGURAÇÃO DE CORES
// ============================================================================

// Função para gerar cores com gradientes únicos por gráfico
const getChartColors = (chartId: string) => ({
  // Gráfico 1: Por área (cores vibrantes com gradientes)
  area: [
    `url(#${chartId}-gradient-purple)`, // Purple
    `url(#${chartId}-gradient-teal)`, // Teal
    `url(#${chartId}-gradient-indigo)`, // Indigo
    `url(#${chartId}-gradient-violet)`, // Violet
    `url(#${chartId}-gradient-pink)`, // Pink
    `url(#${chartId}-gradient-amber)`, // Amber
    `url(#${chartId}-gradient-red)`, // Red
    `url(#${chartId}-gradient-green)`, // Green
  ],
  // Gráfico 2: Por status (cores vibrantes)
  status: [
    `url(#${chartId}-gradient-orange)`, // Orange (Rodando em Time)
    `url(#${chartId}-gradient-red-dark)`, // Red Dark (Em Pausa)
  ],
  // Gráfico 3: Do dia (cores vibrantes)
  today: [
    `url(#${chartId}-gradient-green)`, // Green (Concluídas)
    `url(#${chartId}-gradient-blue)`, // Blue (Com Horas)
    `url(#${chartId}-gradient-red)`, // Red (Canceladas)
  ],
});

/**
 * Gera cores automaticamente baseado no índice
 * Usa HSL para distribuir cores uniformemente no espectro
 * Garante cores vibrantes e distintas mesmo com muitas categorias
 */
const generateColors = (count: number, baseHue: number = 0): string[] => {
  const colors: string[] = [];
  
  for (let i = 0; i < count; i++) {
    // Distribuir uniformemente no espectro HSL (0-360)
    const hue = (baseHue + (i * 360 / count)) % 360;
    
    // Variação de saturação e luminosidade para mais variedade
    // Saturação: 65-85% (cores vibrantes)
    // Luminosidade: 50-60% (cores visíveis)
    const saturation = 65 + (i % 3) * 7; // 65, 72, 79
    const lightness = 50 + (i % 2) * 5; // 50 ou 55
    
    // Converter HSL para RGB
    const hslToRgb = (h: number, s: number, l: number): string => {
      h /= 360;
      s /= 100;
      l /= 100;
      
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs((h * 6) % 2 - 1));
      const m = l - c / 2;
      
      let r = 0, g = 0, b = 0;
      
      if (0 <= h && h < 1/6) {
        r = c; g = x; b = 0;
      } else if (1/6 <= h && h < 2/6) {
        r = x; g = c; b = 0;
      } else if (2/6 <= h && h < 3/6) {
        r = 0; g = c; b = x;
      } else if (3/6 <= h && h < 4/6) {
        r = 0; g = x; b = c;
      } else if (4/6 <= h && h < 5/6) {
        r = x; g = 0; b = c;
      } else if (5/6 <= h && h < 1) {
        r = c; g = 0; b = x;
      }
      
      r = Math.round((r + m) * 255);
      g = Math.round((g + m) * 255);
      b = Math.round((b + m) * 255);
      
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };
    
    colors.push(hslToRgb(hue, saturation, lightness));
  }
  
  return colors;
};

/**
 * Cores fixas para gráficos específicos (status e today)
 * Mantém consistência visual para categorias conhecidas
 */
const CHART_COLORS_SOLID_FIXED = {
  status: [
    '#F59E0B', // Orange (Rodando em Time)
    '#DC2626', // Red Dark (Em Pausa)
  ],
  today: [
    '#10B981', // Green (Concluídas)
    '#3B82F6', // Blue (Com Horas)
    '#EF4444', // Red (Canceladas)
  ],
};

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

interface PieChartComponentProps {
  data: ChartDataItem[];
  colors: string[];
  title: string;
  isLoading: boolean;
  error: any;
  chartId: string; // ID único para cada gráfico
}

const PieChartComponent: React.FC<PieChartComponentProps> = ({
  data,
  colors,
  title,
  isLoading,
  error,
  chartId,
}) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
        <LoadingSpinner size="md" />
        <p className="mt-2 text-sm text-text-muted">Carregando dados...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
        <AlertCircle className="h-8 w-8 text-red-400 mb-2" />
        <p className="text-sm text-red-400">Erro ao carregar dados</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
        <p className="text-sm text-text-muted">Nenhum dado disponível</p>
      </div>
    );
  }

  // Calcular total para porcentagem (deve estar antes de usar)
  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Custom Label moderno com porcentagem
  const renderCustomLabel = (entry: any) => {
    const percent = ((entry.value / total) * 100).toFixed(0);
    return `${percent}%`;
  };

  // Custom Tooltip moderno
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percent = ((data.value / total) * 100).toFixed(1);
      const color = solidColors[data.payload.index % solidColors.length];
      return (
        <div className="bg-background-glass border border-border-glass rounded-lg p-4 shadow-xl backdrop-blur-lg">
          <div className="flex items-center gap-2 mb-2">
            <div 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: color }}
            />
            <p className="font-bold text-text-primary text-base">{data.name}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-text-secondary">
              Quantidade: <span className="font-bold text-lg" style={{ color }}>{data.value}</span>
            </p>
            <p className="text-xs text-text-muted">
              <span className="font-semibold">{percent}%</span> do total
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Determinar qual gráfico é baseado no chartId (mais confiável)
  const isArea = chartId === 'chart-area';
  const isStatus = chartId === 'chart-status';
  const isToday = chartId === 'chart-today';
  
  // Obter cores com gradientes únicos para este gráfico (apenas para status e today)
  const chartColors = getChartColors(chartId);
  
  // Gerar cores automaticamente se for área (pode ter muitas)
  // Ou usar cores fixas para status e today
  let solidColors: string[];
  let gradientColors: string[];
  
  if (isArea) {
    // Gerar cores automaticamente baseado na quantidade de dados
    const colorCount = Math.max(data.length, 8); // Mínimo 8 cores
    solidColors = generateColors(colorCount, 240); // Começar do azul (240°)
    
    // Gerar gradientes dinamicamente
    gradientColors = data.map((_, index) => {
      const color = solidColors[index % solidColors.length];
      // Converter hex para RGB para criar gradiente
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      
      // Criar cor mais clara para o gradiente
      const lighterR = Math.min(255, r + 40);
      const lighterG = Math.min(255, g + 40);
      const lighterB = Math.min(255, b + 40);
      
      const lighterColor = `#${lighterR.toString(16).padStart(2, '0')}${lighterG.toString(16).padStart(2, '0')}${lighterB.toString(16).padStart(2, '0')}`;
      
      return `url(#${chartId}-gradient-custom-${index})`;
    });
    
    // Adicionar gradientes customizados ao defs
    // (será feito no JSX abaixo)
  } else if (isStatus) {
    gradientColors = chartColors.status;
    solidColors = CHART_COLORS_SOLID_FIXED.status;
  } else {
    gradientColors = chartColors.today;
    solidColors = CHART_COLORS_SOLID_FIXED.today;
  }

  return (
    <div className="w-full h-full relative">
      <h4 className="text-sm font-semibold text-text-primary mb-3 text-center">
        {title}
      </h4>
      <ResponsiveContainer width="100%" height={380}>
        <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
          <defs>
            {/* Gradientes fixos para status e today */}
            {!isArea && (
              <>
                <linearGradient id={`${chartId}-gradient-orange`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FB923C" stopOpacity={1} />
                  <stop offset="100%" stopColor="#F97316" stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id={`${chartId}-gradient-red-dark`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#EF4444" stopOpacity={1} />
                  <stop offset="100%" stopColor="#DC2626" stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id={`${chartId}-gradient-green`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#34D399" stopOpacity={1} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id={`${chartId}-gradient-blue`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#60A5FA" stopOpacity={1} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id={`${chartId}-gradient-red`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#F87171" stopOpacity={1} />
                  <stop offset="100%" stopColor="#EF4444" stopOpacity={0.8} />
                </linearGradient>
              </>
            )}
            
            {/* Gradientes dinâmicos para área (gerados automaticamente) */}
            {isArea && data.map((_, index) => {
              const color = solidColors[index % solidColors.length];
              // Converter hex para RGB
              const r = parseInt(color.slice(1, 3), 16);
              const g = parseInt(color.slice(3, 5), 16);
              const b = parseInt(color.slice(5, 7), 16);
              
              // Criar cor mais clara para o gradiente (brilho)
              const lighterR = Math.min(255, Math.round(r * 1.3));
              const lighterG = Math.min(255, Math.round(g * 1.3));
              const lighterB = Math.min(255, Math.round(b * 1.3));
              
              const lighterColor = `rgb(${lighterR}, ${lighterG}, ${lighterB})`;
              
              return (
                <linearGradient 
                  key={`gradient-${index}`}
                  id={`${chartId}-gradient-custom-${index}`} 
                  x1="0%" 
                  y1="0%" 
                  x2="100%" 
                  y2="100%"
                >
                  <stop offset="0%" stopColor={lighterColor} stopOpacity={1} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.9} />
                </linearGradient>
              );
            })}
          </defs>
          <Pie
            data={data}
            cx="50%"
            cy="45%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={110}
            innerRadius={40}
            paddingAngle={3}
            fill="#8884d8"
            dataKey="value"
            animationBegin={0}
            animationDuration={1000}
            animationEasing="ease-out"
            startAngle={90}
            endAngle={-270}
          >
            {data.map((entry, index) => {
              const isLargeSlice = (entry.value / total) > 0.3; // Destacar fatias grandes
              return (
                <Cell
                  key={`cell-${index}`}
                  fill={gradientColors[index % gradientColors.length]}
                  stroke={solidColors[index % solidColors.length]}
                  strokeWidth={isLargeSlice ? 3 : 2}
                  style={{
                    filter: `drop-shadow(0px ${isLargeSlice ? '6px 12px' : '4px 8px'} rgba(0, 0, 0, ${isLargeSlice ? 0.3 : 0.2}))`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    transform: isLargeSlice ? 'scale(1.05)' : 'scale(1)',
                  }}
                  onMouseEnter={(e: any) => {
                    e.target.style.transform = 'scale(1.1) translateZ(10px)';
                    e.target.style.filter = 'drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.4))';
                    e.target.style.zIndex = '10';
                  }}
                  onMouseLeave={(e: any) => {
                    e.target.style.transform = isLargeSlice ? 'scale(1.05)' : 'scale(1)';
                    e.target.style.filter = `drop-shadow(0px ${isLargeSlice ? '6px 12px' : '4px 8px'} rgba(0, 0, 0, ${isLargeSlice ? 0.3 : 0.2}))`;
                    e.target.style.zIndex = '1';
                  }}
                />
              );
            })}
          </Pie>
          <Tooltip 
            content={<CustomTooltip />}
            cursor={{ stroke: '#fff', strokeWidth: 2 }}
          />
          <Legend
            verticalAlign="bottom"
            height={60}
            iconType="circle"
            wrapperStyle={{ paddingTop: '20px' }}
            formatter={(value, entry: any) => {
              const percent = ((entry.payload.value / total) * 100).toFixed(1);
              return (
                <span className="text-text-primary text-xs font-medium">
                  <span 
                    className="inline-block w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: solidColors[entry.payload.index % solidColors.length] }}
                  />
                  {value}: <span className="font-bold">{entry.payload.value}</span> ({percent}%)
                </span>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function AdminDashboardCharts() {
  // Queries para buscar dados dos três gráficos
  const {
    data: chartByAreaData,
    isLoading: loadingArea,
    error: errorArea,
    refetch: refetchArea,
  } = useQuery<{ success: boolean; data: ChartDataItem[] }>({
    queryKey: ['dashboard-chart-by-area'],
    queryFn: async () => {
      const response = await dashboardApi.getChartByArea();
      return response.data;
    },
    refetchInterval: 60 * 1000, // Atualizar a cada minuto
    staleTime: 30 * 1000, // Considerar dados frescos por 30 segundos
  });

  const {
    data: chartByStatusData,
    isLoading: loadingStatus,
    error: errorStatus,
    refetch: refetchStatus,
  } = useQuery<{ success: boolean; data: ChartDataItem[] }>({
    queryKey: ['dashboard-chart-by-status'],
    queryFn: async () => {
      const response = await dashboardApi.getChartByStatus();
      return response.data;
    },
    refetchInterval: 60 * 1000, // Atualizar a cada minuto
    staleTime: 30 * 1000, // Considerar dados frescos por 30 segundos
  });

  const {
    data: chartTodayData,
    isLoading: loadingToday,
    error: errorToday,
    refetch: refetchToday,
  } = useQuery<{ success: boolean; data: ChartDataItem[] }>({
    queryKey: ['dashboard-chart-today'],
    queryFn: async () => {
      const response = await dashboardApi.getChartToday();
      return response.data;
    },
    refetchInterval: 60 * 1000, // Atualizar a cada minuto
    staleTime: 30 * 1000, // Considerar dados frescos por 30 segundos
  });

  // Função para atualizar todos os gráficos
  const handleRefresh = () => {
    refetchArea();
    refetchStatus();
    refetchToday();
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="card-title">Gráficos de OS/JOB/TASK</h3>
            <p className="card-description">
              Visualização de distribuição de ordens de serviço por diferentes métricas
            </p>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 rounded-lg bg-background-glass-hover border border-border-glass hover:border-secondary/30 transition-all duration-300"
            title="Atualizar gráficos"
          >
            <RefreshCw className="h-5 w-5 text-text-secondary hover:text-secondary transition-colors" />
          </button>
        </div>
      </div>
      <div className="card-content">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Gráfico 1: Por Área/Empresa */}
          <div className="bg-gradient-to-br from-background-glass-hover to-background-glass rounded-glass border border-border-glass/50 p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
            <PieChartComponent
              data={chartByAreaData?.data || []}
              colors={[]} // Cores serão geradas automaticamente
              title="Total por Área/Empresa"
              isLoading={loadingArea}
              error={errorArea}
              chartId="chart-area"
            />
          </div>

          {/* Gráfico 2: Por Status em Execução */}
          <div className="bg-gradient-to-br from-background-glass-hover to-background-glass rounded-glass border border-border-glass/50 p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
            <PieChartComponent
              data={chartByStatusData?.data || []}
              colors={getChartColors('chart-status').status}
              title="Em Execução por Status"
              isLoading={loadingStatus}
              error={errorStatus}
              chartId="chart-status"
            />
          </div>

          {/* Gráfico 3: Do Dia */}
          <div className="bg-gradient-to-br from-background-glass-hover to-background-glass rounded-glass border border-border-glass/50 p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
            <PieChartComponent
              data={chartTodayData?.data || []}
              colors={getChartColors('chart-today').today}
              title="Atividades do Dia"
              isLoading={loadingToday}
              error={errorToday}
              chartId="chart-today"
            />
          </div>
        </div>

        {/* Legenda explicativa */}
        <div className="mt-6 pt-6 border-t border-border-glass/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <h5 className="font-semibold text-text-primary mb-2">
                📊 Total por Área/Empresa
              </h5>
              <p className="text-text-muted text-xs">
                Mostra a distribuição de OS/JOB/TASK por área da empresa, excluindo as concluídas e canceladas.
              </p>
            </div>
            <div>
              <h5 className="font-semibold text-text-primary mb-2">
                ⚡ Em Execução por Status
              </h5>
              <p className="text-text-muted text-xs">
                Separa as OS/JOB/TASK em execução por status: rodando em time (EM_ANDAMENTO) e em pausa (PAUSADA).
              </p>
            </div>
            <div>
              <h5 className="font-semibold text-text-primary mb-2">
                📅 Atividades do Dia
              </h5>
              <p className="text-text-muted text-xs">
                Exibe as OS/JOB/TASK do dia: concluídas hoje, com horas registradas hoje e canceladas hoje.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

